/**
 * CENTRALIZED BRANDING CONFIGURATION
 * 
 * This file contains all customizable branding elements for white-labeling the platform.
 * To customize for a specific customer, simply modify the values in this file.
 * 
 * Logo paths defined here are automatically injected into:
 * - client/public/index.html (favicon, apple-touch-icons)
 * - client/public/manifest.json (PWA icons)
 * 
 * After modifying logo paths in this file:
 * 1. Run: npm run inject-branding (or it runs automatically during build)
 * 2. Replace logo files in /client/public/ with your custom logos
 * 3. Rebuild the application
 */

export interface BrandingConfig {
  // Company Information
  companyName: string;
  companyTagline: string;
  companyDescription: string;
  legalEntityName: string; // Used in Terms of Service, Privacy Policy

  // Logo Paths (relative to /public/)
  logos: {
    main: string;           // Main logo used in navbar, headers
    favicon: string;        // Browser favicon
    pwa192: string;         // PWA icon 192x192
    pwa512: string;         // PWA icon 512x512
    appleTouchIcon: string; // Apple touch icon
  };

  // Default Theme Colors
  theme: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    textColor: string;
  };

  // PWA Configuration
  pwa: {
    shortName: string;
    name: string;
    description: string;
    themeColor: string;
    backgroundColor: string;
  };

  // Contact Information
  contact: {
    email: string;
    phone?: string;
    address?: string;
    website?: string;
  };

  // Social Media Links (optional)
  social?: {
    twitter?: string;
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    discord?: string;
  };

  // Footer Configuration
  footer: {
    copyrightText: string;
    tagline: string;
  };

  // Feature Flags (optional customizations)
  features: {
    showApiDocs: boolean;
    showPlayOnline: boolean;
    showOrganizations: boolean;
    showDemoAccount: boolean;
  };
}

/**
 * DEFAULT BRANDING CONFIGURATION
 * 
 * Modify these values to customize the branding for your deployment.
 * All references throughout the app will automatically use these values.
 */
export const branding: BrandingConfig = {
  // ============================================
  // COMPANY INFORMATION
  // ============================================
  companyName: 'ChessDirector',
  companyTagline: 'CHESS TOURNAMENTS',
  companyDescription: 'The complete chess tournament management platform. USCF & FIDE compliant pairings, AI-powered proctoring, payment processing, and everything you need—all in one place.',
  legalEntityName: 'ChessDirector',

  // ============================================
  // LOGO PATHS
  // Replace these files in /client/public/
  // ============================================
  logos: {
    main: '/chessdirector-logo.png',
    favicon: '/favicon-32.png',
    pwa192: '/Gemini_Generated_Image_t20dtwt20dtwt20d-removebg-preview.png',
    pwa512: '/Gemini_Generated_Image_t20dtwt20dtwt20d-removebg-preview.png',
    appleTouchIcon: '/Gemini_Generated_Image_t20dtwt20dtwt20d-removebg-preview.png',
  },

  // ============================================
  // THEME COLORS
  // ============================================
  theme: {
    primaryColor: '#000000',       // Main brand color
    secondaryColor: '#6b7280',     // Secondary/muted color
    accentColor: '#ea580c',        // Accent/highlight color (orange by default)
    backgroundColor: '#ffffff',    // Background color
    textColor: '#1f2937',          // Main text color
  },

  // ============================================
  // PWA CONFIGURATION
  // ============================================
  pwa: {
    shortName: 'ChessDirector',
    name: 'ChessDirector - Chess Tournaments',
    description: 'View live chess tournament standings, pairings, and results',
    themeColor: '#000000',
    backgroundColor: '#ffffff',
  },

  // ============================================
  // CONTACT INFORMATION
  // ============================================
  contact: {
    email: 'support@paircraft.tech',
    phone: undefined,
    address: undefined,
    website: 'https://paircraft.tech',
  },

  // ============================================
  // SOCIAL MEDIA (optional)
  // ============================================
  social: {
    twitter: undefined,
    facebook: undefined,
    instagram: undefined,
    linkedin: undefined,
    discord: undefined,
  },

  // ============================================
  // FOOTER CONFIGURATION
  // ============================================
  footer: {
    copyrightText: `© ${new Date().getFullYear()} ChessDirector. All rights reserved.`,
    tagline: 'The most powerful chess tournament management platform. Built for directors who demand perfection.',
  },

  // ============================================
  // FEATURE FLAGS
  // ============================================
  features: {
    showApiDocs: true,
    showPlayOnline: true,
    showOrganizations: true,
    showDemoAccount: true,
  },
};

/**
 * Helper function to get the full copyright text with current year
 */
export const getCopyrightText = (): string => {
  return `© ${new Date().getFullYear()} ${branding.companyName}. All rights reserved.`;
};

/**
 * Helper function to get the page title
 */
export const getPageTitle = (pageName?: string): string => {
  if (pageName) {
    return `${pageName} | ${branding.companyName}`;
  }
  return `${branding.companyName} - ${branding.pwa.description}`;
};

/**
 * Export a function to update branding at runtime (useful for multi-tenant)
 * This is optional and can be used for dynamic branding based on subdomain
 */
export const updateBranding = (newBranding: Partial<BrandingConfig>): BrandingConfig => {
  return { ...branding, ...newBranding };
};

export default branding;

